import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Typography,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatColorText,
} from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const FONT_SIZES = [
  { label: 'Pequeno', value: '0.875rem' },
  { label: 'Normal', value: '1rem' },
  { label: 'Grande', value: '1.25rem' },
  { label: 'Muito Grande', value: '1.5rem' },
];

const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: 'Times New Roman, serif' },
  { label: 'Helvetica', value: 'Helvetica, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const TEXT_COLORS = [
  { label: 'Preto', value: '#000000' },
  { label: 'Vermelho', value: '#FF0000' },
  { label: 'Verde', value: '#00FF00' },
  { label: 'Azul', value: '#0000FF' },
  { label: 'Amarelo', value: '#FFFF00' },
  { label: 'Roxo', value: '#800080' },
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const [fontSize, setFontSize] = useState('1rem');
  const [fontFamily, setFontFamily] = useState('Arial, sans-serif');
  const [textColor, setTextColor] = useState('#000000');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuType, setMenuType] = useState<'fontFamily' | 'textColor' | null>(null);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, type: 'fontFamily' | 'textColor') => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType(null);
  };

  const handleFormat = (format: string) => {
    if (!textFieldRef.current) return;

    const textarea = textFieldRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    let newText = value;

    // Se não houver texto selecionado, não aplica formatação
    if (start === end) {
      return;
    }

    switch (format) {
      case 'bold':
        // Verifica se o texto já está em negrito
        if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
          // Remove a formatação
          newText = value.substring(0, start) + selectedText.slice(2, -2) + value.substring(end);
        } else {
          // Adiciona a formatação
          newText = value.substring(0, start) + `**${selectedText}**` + value.substring(end);
        }
        break;
      case 'italic':
        // Verifica se o texto já está em itálico
        if (selectedText.startsWith('*') && selectedText.endsWith('*')) {
          // Remove a formatação
          newText = value.substring(0, start) + selectedText.slice(1, -1) + value.substring(end);
        } else {
          // Adiciona a formatação
          newText = value.substring(0, start) + `*${selectedText}*` + value.substring(end);
        }
        break;
      case 'underline':
        // Verifica se o texto já está sublinhado
        if (selectedText.startsWith('__') && selectedText.endsWith('__')) {
          // Remove a formatação
          newText = value.substring(0, start) + selectedText.slice(2, -2) + value.substring(end);
        } else {
          // Adiciona a formatação
          newText = value.substring(0, start) + `__${selectedText}__` + value.substring(end);
        }
        break;
      case 'listBulleted':
        const lines = selectedText.split('\n');
        const bulletedLines = lines.map(line => `- ${line}`).join('\n');
        newText = value.substring(0, start) + bulletedLines + value.substring(end);
        break;
      case 'listNumbered':
        const numberedLines = selectedText.split('\n').map((line, index) => `${index + 1}. ${line}`).join('\n');
        newText = value.substring(0, start) + numberedLines + value.substring(end);
        break;
      default:
        return;
    }

    onChange(newText);
    handleMenuClose();

    // Restaura o foco e a seleção após a formatação
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 2, end + 2);
    }, 0);
  };

  const handleStyleChange = (newValue: string) => {
    switch (menuType) {
      case 'fontFamily':
        setFontFamily(newValue);
        break;
      case 'textColor':
        setTextColor(newValue);
        break;
      default:
        return;
    }
    handleMenuClose();
  };

  return (
    <Box>
      <TextField
        inputRef={textFieldRef}
        multiline
        fullWidth
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        sx={{
          '& .MuiInputBase-root': {
            fontFamily,
            fontSize,
            color: textColor,
          },
        }}
      />

      <Paper sx={{ p: 1, mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Tooltip title="Negrito">
          <IconButton size="small" onClick={() => handleFormat('bold')}>
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="Itálico">
          <IconButton size="small" onClick={() => handleFormat('italic')}>
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sublinhado">
          <IconButton size="small" onClick={() => handleFormat('underline')}>
            <FormatUnderlined />
          </IconButton>
        </Tooltip>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            displayEmpty
            sx={{ height: 32 }}
          >
            {FONT_SIZES.map((size) => (
              <MenuItem key={size.value} value={size.value}>
                <Typography sx={{ fontSize: size.value }}>{size.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Fonte">
          <IconButton size="small" onClick={(e) => handleMenuClick(e, 'fontFamily')}>
            <Typography sx={{ fontFamily }}>Aa</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Cor do Texto">
          <IconButton size="small" onClick={(e) => handleMenuClick(e, 'textColor')}>
            <FormatColorText sx={{ color: textColor }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista com Marcadores">
          <IconButton size="small" onClick={() => handleFormat('listBulleted')}>
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista Numerada">
          <IconButton size="small" onClick={() => handleFormat('listNumbered')}>
            <FormatListNumbered />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinhar à Esquerda">
          <IconButton size="small">
            <FormatAlignLeft />
          </IconButton>
        </Tooltip>
        <Tooltip title="Centralizar">
          <IconButton size="small">
            <FormatAlignCenter />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinhar à Direita">
          <IconButton size="small">
            <FormatAlignRight />
          </IconButton>
        </Tooltip>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuType === 'fontFamily' && FONT_FAMILIES.map((font) => (
          <MenuItem key={font.value} onClick={() => handleStyleChange(font.value)}>
            <Typography sx={{ fontFamily: font.value }}>{font.label}</Typography>
          </MenuItem>
        ))}
        {menuType === 'textColor' && TEXT_COLORS.map((color) => (
          <MenuItem key={color.value} onClick={() => handleStyleChange(color.value)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: color.value,
                  border: '1px solid #ccc',
                  borderRadius: 1,
                }}
              />
              <Typography>{color.label}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default RichTextEditor; 